import definitions from '@frontend/assets/json/portfolio_checkup/explanations.json';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import * as Sentry from '@sentry/vue';
import { makePostRequest } from '@frontend/api/api.js';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks.js';

export const GRADES = {
  GOOD: 'Portfolios Are Similar',
  OK: 'Portfolios Have Some Differences',
  BAD: 'Portfolios Have Substantial Differences',
};

export const GRADE_COLORS = {
  [GRADES.GOOD]: { border: 'success-top', hexColor: '27A683', icon: 'check' },
  [GRADES.OK]: { border: 'warning-top', hexColor: 'EBB516', icon: 'angle-double-up' },
  [GRADES.BAD]: { border: 'danger-top', hexColor: 'A83B5A', icon: 'exclamation-triangle' },
  default: { border: '', hexColor: '999999', icon: '' },
};

function getGradeStyle(grade) {
  const defaultStyle = GRADE_COLORS.default;
  if (!grade) return defaultStyle;
  return GRADE_COLORS[grade] || defaultStyle;
}

export const PORTFOLIO_COLORS = {
  portfolioDark: 'rgb(64, 128, 216)', // Darkened by 40 units
  benchmarkDark: 'rgb(100, 63, 199)', // Darkened by 40 units
  portfolio: 'rgb( 64, 182, 216)',
  benchmark: 'rgb(140, 103, 239)',
  difference: 'rgb(0, 0, 0)',
};

export const RISK_LEVELS = [
  { value: 5, label: 'More Conservative' },
  { value: 10, label: 'Conservative' },
  { value: 30, label: 'Moderately Conservative' },
  { value: 50, label: 'Moderate' },
  { value: 70, label: 'Moderately Aggressive' },
  { value: 90, label: 'Aggressive' },
  { value: 95, label: 'More Aggressive' },
];

function getExpenseRatioGrade(averageFee, benchAverageFee) {
  // const difference = Math.abs(averageFee - benchAverageFee);
  // if (difference > 0.5) return GRADES.BAD;
  // if (difference > 0.15) return GRADES.OK;
  // return GRADES.GOOD;
  return null;
}

function cumulateFees(portfolio, extraFees) {
  let totalFees = portfolio.total_dollars * (portfolio.average_expense_ratio + extraFees);
  let yearFee = totalFees;
  const table = [];
  for (let i = 0; i < 21; i++) {
    table.push(Math.round(totalFees * 100) / 100);
    yearFee *= 1.08;
    totalFees += yearFee;
  }
  return table;
}

export function getExpenseRatio(portfolio, benchmark, optimizedPortfolio, portfolioFees, benchmarkFees, optimizedFees, advisorStore) {
  console.log('PORTFOLIO INPUT', portfolio);
  console.log('BENCHMARK INPUT', benchmark);
  console.log('OPTIMIZED INPUT', optimizedPortfolio);
  const extraPortfolioFees = portfolioFees ? Object.values(portfolioFees).reduce((acc, fee) => acc + parseFloat(fee / 100), 0) : 0;
  const extraBenchmarkFees = benchmarkFees ? Object.values(benchmarkFees).reduce((acc, fee) => acc + parseFloat(fee / 100), 0) : 0;
  const extraOptimizedFees = optimizedFees ? Object.values(optimizedFees).reduce((acc, fee) => acc + parseFloat(fee / 100), 0) : 0;

  const totalPortfolioFees = portfolio ? cumulateFees(portfolio, extraPortfolioFees) : [];
  const totalBenchmarkFees = benchmark ? cumulateFees(benchmark, extraBenchmarkFees) : [];
  const totalOptimizedFees = optimizedPortfolio ? cumulateFees(optimizedPortfolio, extraOptimizedFees) : [];
  const portfolioExpenseRatio = portfolio ? portfolio.average_expense_ratio * 100 : 0;
  const benchmarkExpenseRatio = benchmark ? benchmark.average_expense_ratio * 100 : 0;
  const optimizedExpenseRatio = optimizedPortfolio ? optimizedPortfolio.average_expense_ratio * 100 : 0;
  const current = portfolio ? [{ value: portfolioExpenseRatio }] : [];
  const proposal = benchmark ? [{ value: benchmarkExpenseRatio }] : [];
  const optimized = optimizedPortfolio ? [{ value: optimizedExpenseRatio }] : [];
  const grade = portfolio && benchmark ? getExpenseRatioGrade(portfolioExpenseRatio, benchmarkExpenseRatio) : null;

  console.log('EXPENSE RATIOS*');
  console.log(totalBenchmarkFees);
  console.log(totalPortfolioFees);
  console.log(totalOptimizedFees);

  // Normalize benchmark fees or use original fees if no normalization is possible
  let normalizedBenchmarkExpenseRatio = totalBenchmarkFees;
  if (benchmark && (portfolio || optimizedPortfolio)) {
    const normalizationPortfolio = portfolio || optimizedPortfolio;
    const normalizationRatio = normalizationPortfolio.total_dollars / benchmark.total_dollars;
    normalizedBenchmarkExpenseRatio = totalBenchmarkFees.map(fee => fee * normalizationRatio);
  }

  let normalizedOptimizedExpenseRatio = totalOptimizedFees;
  if (optimizedPortfolio && portfolio) {
    const normalizationPortfolio = portfolio;
    const normalizationRatio = normalizationPortfolio.total_dollars / optimizedPortfolio.total_dollars;
    normalizedOptimizedExpenseRatio = totalOptimizedFees.map(fee => fee * normalizationRatio);
  }

  // Calculate savings after normalizing
  let cumulative_fee_difference = 0;

  // Order of savings calculation priority is:
  // 1. Portfolio vs. Optimized
  // 2. Portfolio vs. Benchmark
  // 3. Optimized vs. Benchmark
  if (totalPortfolioFees.length && normalizedOptimizedExpenseRatio.length) {
    const i = totalPortfolioFees.length - 1;
    cumulative_fee_difference = totalPortfolioFees[i] - normalizedOptimizedExpenseRatio[i];
  } else if (totalPortfolioFees.length && normalizedBenchmarkExpenseRatio.length) {
    const i = totalPortfolioFees.length - 1;
    cumulative_fee_difference = totalPortfolioFees[i] - normalizedBenchmarkExpenseRatio[i];
  } else if (totalOptimizedFees.length && normalizedBenchmarkExpenseRatio.length) {
    const i = totalOptimizedFees.length - 1;
    cumulative_fee_difference = totalOptimizedFees[i] - normalizedBenchmarkExpenseRatio[i];
  }

  const chartData = {
    labels: Array.from(Array(21).keys()).map(n => n.toString()),
    datasets: [],
  };

  if (portfolio) {
    chartData.datasets.push({
      label: portfolio.name,
      data: totalPortfolioFees,
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      fill: false,
      tension: 0.1,
    });
  }

  if (benchmark) {
    console.log('NORMALIZED BENCHMARK EXPENSE RATIO', normalizedBenchmarkExpenseRatio);
    chartData.datasets.push({
      label: benchmark.name,
      data: normalizedBenchmarkExpenseRatio,
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      fill: false,
      tension: 0.1,
    });
  }

  if (optimizedPortfolio) {
    chartData.datasets.push({
      label: optimizedPortfolio.name,
      data: normalizedOptimizedExpenseRatio,
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      fill: false,
      tension: 0.1,
    });
  }

  const output = {
    chartData,
    grade,
    current,
    proposal,
    optimized,
    cumulative_fee_difference: Math.abs(cumulative_fee_difference),
    isPercent: true,
    definition: definitions['Expense Ratio'],
    style: getGradeStyle(grade),
    methodology: 'FeesMethodology',
    portfolioAdditionalFees: extraPortfolioFees,
    benchmarkAdditionalFees: extraBenchmarkFees,
    proposedAdditionalFees: extraOptimizedFees,
  };

  return output;
}


const OVERWEIGHT_THRESHOLD = 0.1;
const UNDERWEIGHT_THRESHOLD = 0.01;
const MAX_DEVIATIONS = 2;

const getDiversificationStatus = (userSectorValue, benchmarkSectorValue) => {
  if (userSectorValue - benchmarkSectorValue > OVERWEIGHT_THRESHOLD) {
    return 'Overweight';
  } else if (userSectorValue < UNDERWEIGHT_THRESHOLD || userSectorValue - benchmarkSectorValue < -OVERWEIGHT_THRESHOLD) {
    return 'Underweight';
  }
  return 'Normal';
};


const getPortfolioDiversification = (bySector, benchmarkBySector) => {
  let diversificationStatus = GRADES.GOOD;
  const overweightSectors = [];
  const underweightSectors = [];
  const sectors = Object.keys(bySector);
  sectors.forEach(sector => {
    switch (getDiversificationStatus(bySector[sector], benchmarkBySector[sector])) {
      case 'Overweight':
        overweightSectors.push(sector);
        if (diversificationStatus === GRADES.GOOD) {
          diversificationStatus = GRADES.OK;
        }
        break;
      case 'Underweight':
        underweightSectors.push(sector);
        if (diversificationStatus === GRADES.GOOD) {
          diversificationStatus = GRADES.OK;
        }
        break;
    }
    if (overweightSectors.length + underweightSectors.length > MAX_DEVIATIONS) {
      diversificationStatus = GRADES.BAD;
    }
  });
  return { diversificationStatus, overweightSectors, underweightSectors };
};


export function getSectorBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const bySector = portfolio?.sector_market_cap_asset_class_breakdown?.['sector_breakdown'] || {};
  const benchmarkBySector = benchmark?.sector_market_cap_asset_class_breakdown?.['sector_breakdown'] || {};
  const optimizedBySector = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['sector_breakdown'] || {};
  const sectors = Object.keys({ ...bySector, ...benchmarkBySector, ...optimizedBySector });

  const current = portfolio ? sectors.map(sector => ({
    name: sector,
    value: (bySector[sector] || 0) * 100,
  })) : [];

  const proposal = benchmark ? sectors.map(sector => ({
    name: sector,
    value: (benchmarkBySector[sector] || 0) * 100,
  })) : [];

  const optimized = optimizedPortfolio ? sectors.map(sector => ({
    name: sector,
    value: (optimizedBySector[sector] || 0) * 100,
  })) : [];

  const grade = portfolio && benchmark ? getPortfolioDiversification(bySector, benchmarkBySector).diversificationStatus : null;

  const chartData = {
    labels: sectors,
    datasets: [],
  };

  if (portfolio) {
    chartData.datasets.push({
      label: portfolio.name || 'Portfolio',
      data: sectors.map(sector => bySector[sector] || 0),
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      key: 'sectors',
    });
  }

  if (benchmark) {
    chartData.datasets.push({
      label: benchmark.name || 'Benchmark',
      data: sectors.map(sector => benchmarkBySector[sector] || 0),
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      key: 'sectors',
    });
  }

  if (optimizedPortfolio) {
    chartData.datasets.push({
      label: optimizedPortfolio.name || 'Optimized',
      data: sectors.map(sector => optimizedBySector[sector] || 0),
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      key: 'sectors',
    });
  }

  const output = {
    chartData: chartData.datasets.length > 0 ? chartData : null,
    grade,
    current,
    proposal,
    optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Sector Diversification'],
    style: getGradeStyle(null),
    methodology: 'SectorMethodology',
  };
  return output;
}


export function getValuesAlignments(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byValue = portfolio?.values_alignments || {};
  const benchmarkByValue = benchmark?.values_alignments || {};
  const optimizedByValue = optimizedPortfolio?.values_alignments || {};
  console.log('VALUES ALIGNMENT DATA', byValue, benchmarkByValue, optimizedByValue);

  let values = Object.keys({ ...byValue, ...benchmarkByValue, ...optimizedByValue });
  // If there is only one value set selected, remove the Overall data.
  if (values.length === 2 && values.includes('Overall')) {
    values = values.filter(v => v !== 'Overall');
  }

  const current = portfolio ? values.map(value => ({ name: value, value: (byValue[value] || 0) * 10 })) : [];
  const proposal = benchmark ? values.map(value => ({ name: value, value: (benchmarkByValue[value] || 0) * 10 })) : [];
  const optimized = optimizedPortfolio ? values.map(value => ({ name: value, value: (optimizedByValue[value] || 0) * 10 })) : [];

  const grade = null; // GRADES.OK;
  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];

  values.forEach(value => {
    if (portfolio) portfolioData.push((byValue?.[value] || 0) / 10);
    if (benchmark) benchmarkData.push((benchmarkByValue?.[value] || 0) / 10);
    if (optimizedPortfolio) optimizedData.push((optimizedByValue?.[value] || 0) / 10);
  });
  console.log('FIRM ADVISOR CORE SETTINGS', advisorStore.firmAdvisorCoreSettings);

  const chartData = {
    labels: values,
    datasets: [],
  };

  if (portfolio) {
    chartData.datasets.push({
      label: portfolio.name || 'Portfolio',
      data: portfolioData,
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      key: 'values',
    });
  }

  if (benchmark) {
    chartData.datasets.push({
      label: benchmark.name || 'Benchmark',
      data: benchmarkData,
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      key: 'values',
    });
  }

  if (optimizedPortfolio) {
    chartData.datasets.push({
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedData,
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      key: 'values',
    });
  }

  const output = {
    chartData,
    grade,
    current,
    proposal,
    optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: null, // definitions['Values Alignment'],
    style: getGradeStyle(null),
    methodology: 'ValuesMethodology',
  };
  return output;
}


export function getAssetClassBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byAssetClass = portfolio?.sector_market_cap_asset_class_breakdown?.['asset_class_breakdown'] || {};
  const benchmarkByAssetClass = benchmark?.sector_market_cap_asset_class_breakdown?.['asset_class_breakdown'] || {};
  const optimizedByAssetClass = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['asset_class_breakdown'] || {};
  let assetClasses = Object.keys({ ...byAssetClass, ...benchmarkByAssetClass, ...optimizedByAssetClass });
  assetClasses = orderLabels(assetClasses, ['US Stocks', 'International Stocks', 'Bonds', 'Cash', 'Alternatives', 'Unclassified']);
  const current = portfolio ? assetClasses.map(assetClass => ({
    name: assetClass,
    value: (byAssetClass[assetClass] || 0) * 100,
  })) : [];

  const proposal = benchmark ? assetClasses.map(assetClass => ({
    name: assetClass,
    value: (benchmarkByAssetClass[assetClass] || 0) * 100,
  })) : [];


  const optimized = optimizedPortfolio ? assetClasses.map(assetClass => ({
    name: assetClass,
    value: (optimizedByAssetClass[assetClass] || 0) * 100,
  })) : [];

  const grade = portfolio && benchmark ? getAssetAllocationGrade(byAssetClass, benchmarkByAssetClass) : null;

  const colors = [
    '#4A90E2', // Soft Blue
    '#FF6B6B', // Vibrant Coral
    '#F9A825', // Golden Yellow
    '#4CAF50', // Muted Green
    '#9B51E0', // Rich Purple
    '#BDBDBD', // Neutral Gray
  ];

  const colorsByAssetClass = {
    'US Stocks': '#4A90E2',
    'International Stocks': '#FF6B6B',
    'Bonds': '#F9A825',
    'Cash': '#4CAF50',
    'Alternatives': '#9B51E0',
    'Unclassified': '#BDBDBD',
  };

  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];
  const portfolioSeries = []; // For Highcharts
  const benchmarkSeries = []; // For Highcharts
  const optimizedSeries = []; // For Highcharts

  assetClasses.forEach(assetClass => {
    const portfolioValue = byAssetClass?.[assetClass] || 0;
    const benchmarkValue = benchmarkByAssetClass?.[assetClass] || 0;
    const optimizedValue = optimizedByAssetClass?.[assetClass] || 0;

    if (portfolio) portfolioData.push(portfolioValue);
    if (benchmark) benchmarkData.push(benchmarkValue);
    if (optimizedPortfolio) optimizedData.push(optimizedValue);

    if (portfolio && portfolioValue) portfolioSeries.push({ name: assetClass, y: portfolioValue, color: colorsByAssetClass[assetClass] });
    if (benchmark && benchmarkValue) benchmarkSeries.push({ name: assetClass, y: benchmarkValue, color: colorsByAssetClass[assetClass] });
    if (optimizedPortfolio && optimizedValue) optimizedSeries.push({ name: assetClass, y: optimizedValue, color: colorsByAssetClass[assetClass] });
  });


  const chartDataForPortfolio = portfolio ? {
    labels: assetClasses,
    datasets: [{
      label: portfolio.name || 'Portfolio',
      data: portfolioData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetClasses',
    }],
  } : null;

  const chartDataForBenchmark = benchmark ? {
    labels: assetClasses,
    datasets: [{
      label: benchmark.name || 'Benchmark',
      data: benchmarkData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetClasses',
    }],
  } : null;


  const chartDataForOptimized = optimizedPortfolio ? {
    labels: assetClasses,
    datasets: [{
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetClasses',
    }],
  } : null;

  const seriesDataForHighcharts = [];

  if (portfolio) {
    seriesDataForHighcharts.push({
      name: portfolio.name || 'Portfolio',
      data: portfolioSeries,
    });
  }

  if (benchmark) {
    seriesDataForHighcharts.push({
      name: benchmark.name || 'Benchmark',
      data: benchmarkSeries,
    });
  }

  if (optimizedPortfolio) {
    seriesDataForHighcharts.push({
      name: optimizedPortfolio.name || 'Optimized',
      data: optimizedSeries,
    });
  }

  // This ensures that the pie charts are evenly spaced horizontally regardless of how many there are
  const pieCount = seriesDataForHighcharts.length;
  const pieSize = 100;

  if (pieCount > 1) {
    let margin;
    if (pieCount === 2) {
      margin = 25;
    } else if (pieCount === 3) {
      margin = 15;
    }
    const usableWidth = 100 - margin * 2;
    const gap = usableWidth / (pieCount - 1); // Gap between pies

    for (let i = 0; i < pieCount; i++) {
      const series = seriesDataForHighcharts[i];
      const x = margin + gap * i;
      console.log('X IN CLASS');
      console.log(x);
      series.center = [`${x}%`, '50%']; // Position of the center of the pie
    }
    //
  } else {
    seriesDataForHighcharts[0].center = ['50%', '50%'];
  }

  const output = {
    chartDataForPortfolio,
    chartDataForBenchmark,
    chartDataForOptimized,
    seriesDataForHighcharts,
    pieSize,
    grade, current, proposal, optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Asset Allocation'],
    style: getGradeStyle(null),
    methodology: 'AssetClassMethodology',
  };
  return output;
}


export function getAssetLocationBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byTaxableAssetClass = portfolio?.sector_market_cap_asset_class_breakdown?.['taxable_asset_class_breakdown'] || {};
  const byTaxDeferredAssetClass = portfolio?.sector_market_cap_asset_class_breakdown?.['tax_deferred_asset_class_breakdown'] || {};
  const byTaxFreeAssetClass = portfolio?.sector_market_cap_asset_class_breakdown?.['tax_free_asset_class_breakdown'] || {};
  const optimizedPortfolioByTaxableAssetClass = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['taxable_asset_class_breakdown'] || {};
  const optimizedPortfolioByTaxDeferredAssetClass = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['tax_deferred_asset_class_breakdown'] || {};
  const optimizedPortfolioByTaxFreeAssetClass = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['tax_free_asset_class_breakdown'] || {};
  const portfolioHoldingsWithNoTaxStatus = portfolio?.sector_market_cap_asset_class_breakdown?.['holdings_with_no_tax_status'] || [];
  const optimizedPortfolioHoldingsWithNoTaxStatus = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['holdings_with_no_tax_status'] || [];
  let assetLocations = Object.keys({ ...byTaxableAssetClass, ...byTaxDeferredAssetClass, ...byTaxFreeAssetClass, ...optimizedPortfolioByTaxableAssetClass, ...optimizedPortfolioByTaxDeferredAssetClass, ...optimizedPortfolioByTaxFreeAssetClass });
  assetLocations = orderLabels(assetLocations, ['US Stocks', 'International Stocks', 'Bonds', 'Cash', 'Alternatives', 'Unclassified']);

  const currentTaxable = portfolio ? assetLocations.map(assetLocation => ({
    name: assetLocation,
    value: (byTaxableAssetClass[assetLocation] || 0) * 100,
  })) : [];

  const currentTaxDeferred = portfolio ? assetLocations.map(assetLocation => ({
    name: assetLocation,
    value: (byTaxDeferredAssetClass[assetLocation] || 0) * 100,
  })) : [];

  const currentTaxFree = portfolio ? assetLocations.map(assetLocation => ({
    name: assetLocation,
    value: (byTaxFreeAssetClass[assetLocation] || 0) * 100,
  })) : [];

  const optimizedPortfolioTaxable = optimizedPortfolio ? assetLocations.map(assetLocation => ({
    name: assetLocation,
    value: (optimizedPortfolioByTaxableAssetClass[assetLocation] || 0) * 100,
  })) : [];

  const optimizedPortfolioTaxDeferred = optimizedPortfolio ? assetLocations.map(assetLocation => ({
    name: assetLocation,
    value: (optimizedPortfolioByTaxDeferredAssetClass[assetLocation] || 0) * 100,
  })) : [];

  const optimizedPortfolioTaxFree = optimizedPortfolio ? assetLocations.map(assetLocation => ({
    name: assetLocation,
    value: (optimizedPortfolioByTaxFreeAssetClass[assetLocation] || 0) * 100,
  })) : [];

  const colors = [
    '#4A90E2', // Soft Blue
    '#FF6B6B', // Vibrant Coral
    '#F9A825', // Golden Yellow
    '#4CAF50', // Muted Green
    '#9B51E0', // Rich Purple
    '#BDBDBD', // Neutral Gray
    '#FF8C00', // Dark Orange for Taxable Bonds
    '#8A2BE2', // Blue Violet for Municipal Bonds
    '#008080', // Teal
  ];

  const colorsByAssetClass = {
    'US Stocks': '#4A90E2',
    'International Stocks': '#FF6B6B',
    'Taxable Bonds': '#FF8C00',
    'Municipal Bonds': '#008080',
    'Cash': '#4CAF50',
    'Alternatives': '#9B51E0',
    'Unclassified': '#BDBDBD',
  };

  const portfolioTaxableData = [];
  const portfolioTaxDeferredData = [];
  const portfolioTaxFreeData = [];
  const optimizedPortfolioTaxableData = [];
  const optimizedPortfolioTaxDeferredData = [];
  const optimizedPortfolioTaxFreeData = [];
  const portfolioTaxableSeries = [];
  const portfolioTaxDeferredSeries = [];
  const portfolioTaxFreeSeries = [];
  const optimizedPortfolioTaxableSeries = [];
  const optimizedPortfolioTaxDeferredSeries = [];
  const optimizedPortfolioTaxFreeSeries = [];

  assetLocations.forEach(assetLocation => {
    const portfolioTaxableValue = byTaxableAssetClass?.[assetLocation] || 0;
    const portfolioTaxDeferredValue = byTaxDeferredAssetClass?.[assetLocation] || 0;
    const portfolioTaxFreeValue = byTaxFreeAssetClass?.[assetLocation] || 0;
    const optimizedPortfolioTaxableValue = optimizedPortfolioByTaxableAssetClass?.[assetLocation] || 0;
    const optimizedPortfolioTaxDeferredValue = optimizedPortfolioByTaxDeferredAssetClass?.[assetLocation] || 0;
    const optimizedPortfolioTaxFreeValue = optimizedPortfolioByTaxFreeAssetClass?.[assetLocation] || 0;

    if (portfolio) portfolioTaxableData.push(portfolioTaxableValue);
    if (portfolio) portfolioTaxDeferredData.push(portfolioTaxDeferredValue);
    if (portfolio) portfolioTaxFreeData.push(portfolioTaxFreeValue);
    if (optimizedPortfolio) optimizedPortfolioTaxableData.push(optimizedPortfolioTaxableValue);
    if (optimizedPortfolio) optimizedPortfolioTaxDeferredData.push(optimizedPortfolioTaxDeferredValue);
    if (optimizedPortfolio) optimizedPortfolioTaxFreeData.push(optimizedPortfolioTaxFreeValue);

    if (portfolio && portfolioTaxableValue) portfolioTaxableSeries.push({ name: assetLocation, y: portfolioTaxableValue, color: colorsByAssetClass[assetLocation] });
    if (portfolio && portfolioTaxDeferredValue) portfolioTaxDeferredSeries.push({ name: assetLocation, y: portfolioTaxDeferredValue, color: colorsByAssetClass[assetLocation] });
    if (portfolio && portfolioTaxFreeValue) portfolioTaxFreeSeries.push({ name: assetLocation, y: portfolioTaxFreeValue, color: colorsByAssetClass[assetLocation] });
    if (optimizedPortfolio && optimizedPortfolioTaxableValue) optimizedPortfolioTaxableSeries.push({ name: assetLocation, y: optimizedPortfolioTaxableValue, color: colorsByAssetClass[assetLocation] });
    if (optimizedPortfolio && optimizedPortfolioTaxDeferredValue) optimizedPortfolioTaxDeferredSeries.push({ name: assetLocation, y: optimizedPortfolioTaxDeferredValue, color: colorsByAssetClass[assetLocation] });
    if (optimizedPortfolio && optimizedPortfolioTaxFreeValue) optimizedPortfolioTaxFreeSeries.push({ name: assetLocation, y: optimizedPortfolioTaxFreeValue, color: colorsByAssetClass[assetLocation] });
  });

  const chartDataForTaxablePortfolio = portfolio ? {
    labels: assetLocations,
    datasets: [{
      label: portfolio.name || 'Portfolio Taxable',
      data: portfolioTaxableData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetLocations',
    }],
  } : null;
  const chartDataForTaxDeferredPortfolio = portfolio ? {
    labels: assetLocations,
    datasets: [{
      label: portfolio.name || 'Portfolio Tax Deferred',
      data: portfolioTaxDeferredData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetLocations',
    }],
  } : null;
  const chartDataForTaxFreePortfolio = portfolio ? {
    labels: assetLocations,
    datasets: [{
      label: portfolio.name || 'Portfolio Tax Free',
      data: portfolioTaxFreeData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetLocations',
    }],
  } : null;

  const chartDataForTaxableoptimizedPortfolio = optimizedPortfolio ? {
    labels: assetLocations,
    datasets: [{
      label: optimizedPortfolio.name || 'Proposed Taxable',
      data: optimizedPortfolioTaxableData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetLocations',
    }],
  } : null;
  const chartDataForTaxDeferredoptimizedPortfolio = optimizedPortfolio ? {
    labels: assetLocations,
    datasets: [{
      label: optimizedPortfolio.name || 'Proposed Tax Deferred',
      data: optimizedPortfolioTaxDeferredData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetLocations',
    }],
  } : null;
  const chartDataForTaxFreeoptimizedPortfolio = optimizedPortfolio ? {
    labels: assetLocations,
    datasets: [{
      label: optimizedPortfolio.name || 'Proposed Tax Free',
      data: optimizedPortfolioTaxFreeData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'assetLocations',
    }],
  } : null;

  const portfolioSeriesDataForHighcharts = [];
  const optimizedPortfolioSeriesDataForHighcharts = [];

  // Construct chart options for portfolio series
  if (portfolio) {
    if (portfolioTaxableSeries.length) {
      portfolioSeriesDataForHighcharts.push({
        name: 'Taxable',
        data: portfolioTaxableSeries,
      });
    }
    if (portfolioTaxDeferredSeries.length) {
      portfolioSeriesDataForHighcharts.push({
        name: 'Tax Deferred',
        data: portfolioTaxDeferredSeries,
      });
    }
    if (portfolioTaxFreeSeries.length) {
      portfolioSeriesDataForHighcharts.push({
        name: 'Tax Free',
        data: portfolioTaxFreeSeries,
      });
    }
  }

  // Construct chart options for optimizedPortfolio series
  if (optimizedPortfolio) {
    if (optimizedPortfolioTaxableSeries.length) {
      optimizedPortfolioSeriesDataForHighcharts.push({
        name: 'Taxable',
        data: optimizedPortfolioTaxableSeries,
      });
    }
    if (optimizedPortfolioTaxDeferredSeries.length) {
      optimizedPortfolioSeriesDataForHighcharts.push({
        name: 'Tax Deferred',
        data: optimizedPortfolioTaxDeferredSeries,
      });
    }
    if (optimizedPortfolioTaxFreeSeries.length) {
      optimizedPortfolioSeriesDataForHighcharts.push({
        name: 'Tax Free',
        data: optimizedPortfolioTaxFreeSeries,
      });
    }
  }
  const portfolioPieCount = portfolioSeriesDataForHighcharts.length;
  const portfolioPieSize = 100;

  if (portfolioPieCount > 1) {
    let margin;
    if (portfolioPieCount === 2) {
      margin = 25;
    } else if (portfolioPieCount === 3) {
      margin = 15;
    }
    const usableWidth = 100 - margin * 2;
    const gap = usableWidth / (portfolioPieCount - 1); // Gap between pies

    for (let i = 0; i < portfolioPieCount; i++) {
      const series = portfolioSeriesDataForHighcharts[i];
      const x = margin + gap * i;
      series.center = [`${x}%`, '50%']; // Position of the center of the pie
    }

    //
  } else if (portfolioSeriesDataForHighcharts.length) {
    portfolioSeriesDataForHighcharts[0].center = ['50%', '50%'];
  }

  const optimizedPortfolioPieCount = optimizedPortfolioSeriesDataForHighcharts.length;
  const optimizedPortfolioPieSize = 100;

  if (optimizedPortfolioPieCount > 1) {
    let margin;
    if (optimizedPortfolioPieCount === 2) {
      margin = 25;
    } else if (optimizedPortfolioPieCount === 3) {
      margin = 15;
    }
    const usableWidth = 100 - margin * 2;
    const gap = usableWidth / (optimizedPortfolioPieCount - 1); // Gap between pies

    for (let i = 0; i < optimizedPortfolioPieCount; i++) {
      const series = optimizedPortfolioSeriesDataForHighcharts[i];
      const x = margin + gap * i;
      series.center = [`${x}%`, '50%']; // Position of the center of the pie
    }

    //
  } else if (optimizedPortfolioSeriesDataForHighcharts.length) {
    optimizedPortfolioSeriesDataForHighcharts[0].center = ['50%', '50%'];
  }


  const grade = 'A';

  const output = {
    chartDataForTaxablePortfolio,
    chartDataForTaxDeferredPortfolio,
    chartDataForTaxFreePortfolio,
    chartDataForTaxableoptimizedPortfolio,
    chartDataForTaxDeferredoptimizedPortfolio,
    chartDataForTaxFreeoptimizedPortfolio,
    portfolioSeriesDataForHighcharts,
    optimizedPortfolioSeriesDataForHighcharts,
    portfolioPieSize,
    optimizedPortfolioPieSize,
    grade,
    currentTaxable,
    currentTaxDeferred,
    currentTaxFree,
    optimizedPortfolioTaxable,
    optimizedPortfolioTaxDeferred,
    optimizedPortfolioTaxFree,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Asset Location'],
    style: getGradeStyle(null),
    methodology: 'AssetLocationMethodology',
    portfolioHoldingsWithNoTaxStatus,
    optimizedPortfolioHoldingsWithNoTaxStatus,
  };
  return output;
}

function getAssetAllocationGrade(byAssetClass, benchmarkByAssetClass) {
  const allAssets = Object.keys({ ...byAssetClass, ...benchmarkByAssetClass });

  for (const asset of allAssets) {
    const difference = (byAssetClass?.[asset] || 0) - (benchmarkByAssetClass?.[asset] || 0);

    if (difference > 0.25) {
      return GRADES.BAD;
    } else if (difference > 0.15) {
      return GRADES.OK;
    }
  }

  return GRADES.GOOD;
}


export function getReturnsBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byPeriod = portfolio?.trailing_returns_monthly_rebalance?.[0] || {};
  const benchmarkByPeriod = benchmark?.trailing_returns_monthly_rebalance?.[0] || {};
  const optimizedByPeriod = optimizedPortfolio?.trailing_returns_monthly_rebalance?.[0] || {};
  const periods = Object.keys({ ...byPeriod, ...benchmarkByPeriod, ...optimizedByPeriod });

  const periodLabels = periods.map(period => {
    const periodNumber = period.match(/\d+/)?.[0] || '';
    return { label: `${periodNumber} Year`, value: period };
  });

  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];

  periods.forEach(period => {
    const portfolioValue = byPeriod?.[period] || 0;
    const benchmarkValue = benchmarkByPeriod?.[period] || 0;
    const optimizedValue = optimizedByPeriod?.[period] || 0;
    portfolioData.push(portfolioValue * 100);
    benchmarkData.push(benchmarkValue * 100);
    optimizedData.push(optimizedValue * 100);
  });

  const cumulativeDifference = portfolio && benchmark ?
    getCumulativeDifference(portfolioData, benchmarkData) :
    0;
  console.log('cumulativeDifference', cumulativeDifference);
  const grade = getGradeFromDifference(cumulativeDifference);

  const current = portfolio ? periodLabels.map(({ label, value }) => ({
    name: label,
    value: byPeriod[value] ? byPeriod[value] * 100 : 0,
  })) : [];

  const proposal = benchmark ? periodLabels.map(({ label, value }) => ({
    name: label,
    value: benchmarkByPeriod[value] ? benchmarkByPeriod[value] * 100 : 0,
  })) : [];

  const optimized = optimizedPortfolio ? periodLabels.map(({ label, value }) => ({
    name: label,
    value: optimizedByPeriod[value] ? optimizedByPeriod[value] * 100 : 0,
  })) : [];

  const cumulative_fee_difference = 'N/A'; // Turning off savings for returns per request

  const output = {
    chartData: { portfolioData, benchmarkData, optimizedData },
    grade, current, proposal, optimized, cumulative_fee_difference,
    isPercent: true,
    definition: definitions['Returns'],
    style: getGradeStyle(null),
    methodology: 'ReturnsMethodology',
  };

  return output;
}
export function getNewReturnsBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  // This function is needed in case the portfolio does not have the full 10 years of data
  // For example, if there is less than 3 years of data, the keys 5yr_returns and 10yr_returns will be empty.
  function findReturnsData(portfolio) {
    const returnsByPeriod = portfolio?.trailing_returns_monthly_rebalance?.[3];

    // Find the largest data set available
    if (returnsByPeriod) {
      for (const period of ['10', '5', '3', '1']) {
        const data = returnsByPeriod[`${period}_yr_returns`];
        if (data && Object.keys(data).length) return data;
      }
    }
    return null;
  }

  console.log('getNewReturnsBreakdown.portfolio', portfolio);
  console.log('Looking for portfolio returns data');
  const portfolio10YearReturns = findReturnsData(portfolio);
  console.log('Found portfolio returns data', portfolio10YearReturns);
  const portfolioData = portfolio10YearReturns ? Object.entries(portfolio10YearReturns).map(([key, value]) => {
    return [
      new Date(key).getTime(),
      value,
    ];
  }) : [];

  console.log('Looking for benchmark returns data');
  const benchmark10YearReturns = findReturnsData(benchmark);
  console.log('Found benchmark returns data', benchmark10YearReturns);
  const benchmarkData = benchmark10YearReturns ? Object.entries(benchmark10YearReturns).map(([key, value]) => {
    return [
      new Date(key).getTime(),
      value,
    ];
  }) : [];

  console.log('Looking for optimized portfolio returns data');
  const optimized10YearReturns = findReturnsData(optimizedPortfolio);
  console.log('Found optimized portfolio returns data', optimized10YearReturns);
  const optimizedData = optimized10YearReturns ? Object.entries(optimized10YearReturns).map(([key, value]) => {
    return [
      new Date(key).getTime(),
      value,
    ];
  }) : [];

  const output = {
    chartData: { portfolioData, benchmarkData, optimizedData },
    current: [],
    proposal: [],
    cumulative_fee_difference: 'N/A',
    isPercent: true,
    definition: definitions['Returns'],
    style: getGradeStyle(null),
    methodology: 'ReturnsMethodology',
  };

  return output;
}

export function getVolatilityAndReturnsBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const getHoldingsAndTotalDollars = data => {
    if (!data) return { allHoldings: [], totalDollars: 0 };
    const allHoldings = [...(data.holdings || []), ...(data.unmatched_holdings || [])];
    const totalDollars = allHoldings.reduce((acc, holding) => acc + holding.dollars, 0);
    return { allHoldings, totalDollars };
  };

  const getScatterData = (data, allHoldings, totalDollars) => {
    if (!data || !allHoldings) return [];
    return allHoldings.reduce((acc, holding) => {
      if (!holding.ticker) return acc; // Skip holdings without tickers
      const symbol = holding.ticker.symbol;
      const volatility = holding.ticker.trailing_volatilities?.trailing_3_yr_volatility;
      const returnRate = holding.ticker.trailing_returns?.trailing_3_yr_returns;
      if (volatility != null && returnRate != null) {
        const percentageOfPortfolio = holding.dollars / totalDollars * 100;
        acc.push({ x: volatility * 100, y: returnRate * 100, name: symbol, percentageOfPortfolio });
      }
      return acc;
    }, []);
  };

  const getTotalData = (data, name) => {
    if (!data) return [];
    const volatility = data.trailing_volatilities?.[0]?.['trailing_3_yr_volatility'] || 0;
    const returns = data.trailing_returns_monthly_rebalance?.[0]?.['trailing_3_yr_returns'] || 0;
    return [{ x: volatility * 100, y: returns * 100, name: `${name} Total` }];
  };

  const { allHoldings: portfolioHoldings, totalDollars: portfolioTotalDollars } = getHoldingsAndTotalDollars(portfolio);
  const { allHoldings: benchmarkHoldings, totalDollars: benchmarkTotalDollars } = getHoldingsAndTotalDollars(benchmark);
  const { allHoldings: optimizedHoldings, totalDollars: optimizedTotalDollars } = getHoldingsAndTotalDollars(optimizedPortfolio);

  const scatterData = getScatterData(portfolio, portfolioHoldings, portfolioTotalDollars);
  const scatterDataForBenchmark = getScatterData(benchmark, benchmarkHoldings, benchmarkTotalDollars);
  const scatterDataForOptimized = getScatterData(optimizedPortfolio, optimizedHoldings, optimizedTotalDollars);

  const portfolioTotal = getTotalData(portfolio, portfolio?.name || 'Portfolio');
  const benchmarkTotal = getTotalData(benchmark, benchmark?.name || 'Benchmark');
  const optimizedTotal = getTotalData(optimizedPortfolio, optimizedPortfolio?.name || 'Optimized');

  console.log('scatterData', scatterData);
  console.log('portfolioTotal', portfolioTotal);
  console.log('benchmarkTotal', benchmarkTotal);
  console.log('optimizedTotal', optimizedTotal);

  const output = {
    chartData: {
      portfolioData: scatterData,
      benchmarkData: scatterDataForBenchmark,
      optimizedData: scatterDataForOptimized,
      portfolioTotal,
      benchmarkTotal,
      optimizedTotal,
    },
    current: [],
    proposal: [],
    cumulative_fee_difference: 'N/A',
    isPercent: true,
    definition: null,
    style: getGradeStyle(null),
    methodology: 'RiskReturnsMethodology',
  };

  return output;
}

function getCumulativeDifference(portfolioData, benchmarkData) {
  let cumulativeDifference = 0;

  portfolioData.forEach((period, i) => {
    const delta = period - benchmarkData[i];
    cumulativeDifference += delta;
  });

  return cumulativeDifference;
}

// Note here GOOD gets translated to "similar", so we're looking at absolute value, not if the returns are higher.
function getGradeFromDifference(cumulativeDifference) {
  if (Math.abs(cumulativeDifference) < 0.12) {
    return GRADES.GOOD;
  } else if (Math.abs(cumulativeDifference) < 0.2) {
    return GRADES.OK;
  } else {
    return GRADES.BAD;
  }
}


export function getVolatilityBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byPeriod = portfolio?.trailing_volatilities?.[0] || {};
  const benchmarkByPeriod = benchmark?.trailing_volatilities?.[0] || {};
  const optimizedByPeriod = optimizedPortfolio?.trailing_volatilities?.[0] || {};
  const periods = Object.keys({ ...byPeriod, ...benchmarkByPeriod, ...optimizedByPeriod });

  const periodLabels = periods.map(period => {
    const periodNumber = period.match(/\d+/)?.[0] || '';
    return { label: `${periodNumber} Year`, value: period };
  });

  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];

  periods.forEach(period => {
    portfolioData.push(byPeriod[period] || 0);
    benchmarkData.push(benchmarkByPeriod[period] || 0);
    optimizedData.push(optimizedByPeriod[period] || 0);
  });

  const current = periodLabels.map(({ label, value }) => ({
    name: label,
    value: (byPeriod[value] || 0) * 100,
  }));

  const proposal = periodLabels.map(({ label, value }) => ({
    name: label,
    value: (benchmarkByPeriod[value] || 0) * 100,
  }));

  const optimized = periodLabels.map(({ label, value }) => ({
    name: label,
    value: (optimizedByPeriod[value] || 0) * 100,
  }));

  const cumulativeDifference = getCumulativeDifference(portfolioData, benchmarkData);
  const grade = getGradeFromDifference(cumulativeDifference);

  const chartData = {
    labels: periodLabels.map(period => period.label),
    datasets: [
      portfolio && {
        label: portfolio.name || 'Portfolio',
        data: portfolioData,
        borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
        backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
        key: 'trailingVolatilities',
      },
      benchmark && {
        label: benchmark.name || 'Benchmark',
        data: benchmarkData,
        borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
        backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
        key: 'trailingVolatilities',
      },
      optimizedPortfolio && {
        label: optimizedPortfolio.name || 'Optimized',
        data: optimizedData,
        borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
        backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
        key: 'trailingVolatilities',
      },
    ].filter(Boolean),
  };

  const output = {
    chartData,
    grade,
    current,
    proposal,
    optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Volatility'],
    style: getGradeStyle(null),
    methodology: 'VolatilityMethodology',
  };

  return output;
}


export function getMarketCapBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byCategory = portfolio?.sector_market_cap_asset_class_breakdown?.['market_cap_breakdown'] || {};
  const benchmarkByCategory = benchmark?.sector_market_cap_asset_class_breakdown?.['market_cap_breakdown'] || {};
  const optimizedByCategory = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['market_cap_breakdown'] || {};
  let categories = Object.keys({ ...byCategory, ...benchmarkByCategory, ...optimizedByCategory });
  categories = orderLabels(categories, ['Mega Cap', 'Large Cap', 'Mid Cap', 'Small Cap', 'Micro Cap', 'Unclassified']);

  const current = portfolio ? categories.map(category => ({
    name: category,
    value: (byCategory[category] || 0) * 100,
  })) : [];

  const proposal = benchmark ? categories.map(category => ({
    name: category,
    value: (benchmarkByCategory[category] || 0) * 100,
  })) : [];

  const optimized = optimizedPortfolio ? categories.map(category => ({
    name: category,
    value: (optimizedByCategory[category] || 0) * 100,
  })) : [];

  const { diversificationStatus: grade } = getPortfolioDiversification(byCategory, benchmarkByCategory);

  const portfolioData = categories.map(category => byCategory?.[category] || 0);
  const benchmarkData = categories.map(category => benchmarkByCategory?.[category] || 0);
  const optimizedData = categories.map(category => optimizedByCategory?.[category] || 0);

  const chartData = {
    labels: categories,
    datasets: [
      portfolio && {
        label: portfolio.name || 'Portfolio',
        data: portfolioData,
        borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
        backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
        key: 'marketCap',
      },
      benchmark && {
        label: benchmark.name || 'Benchmark',
        data: benchmarkData,
        borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
        backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
        key: 'marketCap',
      },
      optimizedPortfolio && {
        label: optimizedPortfolio.name || 'Optimized',
        data: optimizedData,
        borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
        backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
        key: 'marketCap',
      },
    ].filter(Boolean),
  };

  const output = {
    chartData,
    grade,
    current,
    proposal,
    optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Market Cap Breakdown'],
    style: getGradeStyle(null),
    methodology: 'MarketCapMethodology',
  };
  return output;
}


export function getStyleCategoryBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byStyleCategory = portfolio?.sector_market_cap_asset_class_breakdown?.['style_breakdown'] || {};
  const benchmarkByStyleCategory = benchmark?.sector_market_cap_asset_class_breakdown?.['style_breakdown'] || {};
  const optimizedByStyleCategory = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['style_breakdown'] || {};
  let styleCategories = Object.keys({ ...byStyleCategory, ...benchmarkByStyleCategory, ...optimizedByStyleCategory });
  styleCategories = orderLabels(styleCategories, ['Large Core', 'Large Value', 'Large Growth', 'Mid Core', 'Mid Value', 'Mid Growth', 'Small Core', 'Small Value', 'Small Growth', 'Unclassified']);
  const current = styleCategories.map(styleCategory => {
    return { name: styleCategory, value: (byStyleCategory?.[styleCategory] || 0) * 100 };
  });
  const proposal = styleCategories.map(styleCategory => {
    return { name: styleCategory, value: (benchmarkByStyleCategory?.[styleCategory] || 0) * 100 };
  });
  const optimized = styleCategories.map(styleCategory => {
    return { name: styleCategory, value: (optimizedByStyleCategory?.[styleCategory] || 0) * 100 };
  });

  const grade = 'A';

  const colors = [
    '#4A90E2', // Soft Blue
    '#FF6B6B', // Vibrant Coral
    '#F9A825', // Golden Yellow
    '#4CAF50', // Muted Green
    '#9B51E0', // Rich Purple
    '#BDBDBD', // Neutral Gray
    '#D32F2F', // Red
    '#1976D2', // Blue
    '#388E3C', // Green
    '#F57C00', // Orange
  ];

  const colorsByStyleCategory = {
    'Large Growth': '#4A90E2',
    'Large Value': '#FF6B6B',
    'Large Core': '#F9A825',
    'Mid Growth': '#4CAF50',
    'Mid Value': '#9B51E0',
    'Mid Core': '#D32F2F',
    'Small Growth': '#1976D2',
    'Small Value': '#388E3C',
    'Small Core': '#F57C00',
    'Unclassified': '#BDBDBD',
  };

  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];
  const portfolioSeries = []; // For Highcharts
  const benchmarkSeries = []; // For Highcharts
  const optimizedSeries = []; // For Highcharts

  styleCategories.forEach(styleCategory => {
    const portfolioValue = byStyleCategory?.[styleCategory] || 0;
    const benchmarkValue = benchmarkByStyleCategory?.[styleCategory] || 0;
    const optimizedValue = optimizedByStyleCategory?.[styleCategory] || 0;

    portfolioData.push(portfolioValue);
    benchmarkData.push(benchmarkValue);
    optimizedData.push(optimizedValue);

    if (portfolioValue) portfolioSeries.push({ name: styleCategory, y: portfolioValue, color: colorsByStyleCategory[styleCategory] });
    if (benchmarkValue) benchmarkSeries.push({ name: styleCategory, y: benchmarkValue, color: colorsByStyleCategory[styleCategory] });
    if (optimizedValue) optimizedSeries.push({ name: styleCategory, y: optimizedValue, color: colorsByStyleCategory[styleCategory] });
  });

  const chartDataForPortfolio = portfolio ? {
    labels: styleCategories,
    datasets: [{
      label: portfolio.name || 'Portfolio',
      data: portfolioData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'styleCategories',
    }],
  } : null;

  const chartDataForBenchmark = benchmark ? {
    labels: styleCategories,
    datasets: [{
      label: benchmark.name || 'Benchmark',
      data: benchmarkData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'styleCategories',
    }],
  } : null;

  const chartDataForOptimized = optimizedPortfolio ? {
    labels: styleCategories,
    datasets: [{
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'styleCategories',
    }],
  } : null;

  const seriesDataForHighcharts = [];

  if (portfolio) {
    seriesDataForHighcharts.push({
      name: portfolio.name || 'Portfolio',
      data: portfolioSeries,
      center: ['25%', '50%'], // Position the first pie chart
    });
  }

  if (benchmark) {
    seriesDataForHighcharts.push({
      name: benchmark.name || 'Benchmark',
      data: benchmarkSeries,
      center: ['75%', '50%'], // Position the second pie chart
    });
  }

  if (optimizedPortfolio) {
    seriesDataForHighcharts.push({
      name: optimizedPortfolio.name || 'Optimized',
      data: optimizedSeries,
      center: ['50%', '50%'], // Position the third pie chart
    });
  }

  // This ensures that the pie charts are evenly spaced horizontally regardless of how many there are
  const pieCount = seriesDataForHighcharts.length;
  const pieSize = 100;

  if (pieCount > 1) {
    let margin;
    if (pieCount === 2) {
      margin = 25;
    } else if (pieCount === 3) {
      margin = 15;
    }
    const usableWidth = 100 - margin * 2;
    const gap = usableWidth / (pieCount - 1); // Gap between pies

    for (let i = 0; i < pieCount; i++) {
      const series = seriesDataForHighcharts[i];
      const x = margin + gap * i;
      series.center = [`${x}%`, '50%']; // Position of the center of the pie
    }

    //
  } else {
    seriesDataForHighcharts[0].center = ['50%', '50%'];
  }

  const output = {
    chartDataForPortfolio,
    chartDataForBenchmark,
    chartDataForOptimized,
    seriesDataForHighcharts,
    pieSize,
    grade, current, proposal, optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: null,
    style: getGradeStyle(null),
    methodology: 'StyleCategoryMethodology',
  };
  return output;
}


export function getExtendedStyleBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byExtendedStyle = portfolio?.sector_market_cap_asset_class_breakdown?.['extended_style'] || {};
  const benchmarkbyExtendedStyle = benchmark?.sector_market_cap_asset_class_breakdown?.['extended_style'] || {};
  const optimizedbyExtendedStyle = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['extended_style'] || {};
  let extendedStyles = Object.keys({ ...byExtendedStyle, ...benchmarkbyExtendedStyle, ...optimizedbyExtendedStyle });
  // extendedStyles = orderLabels(extendedStyles, ['US Large Growth', 'US Large Value', 'US Mid Growth', 'US Mid Value', 'US Small Growth', 'US Small Value', 'Developed Market Stock', 'Emerging Market Stock', 'US Investment Grade Bonds', 'US High Yield Bonds', 'Non-US Bonds', 'Real Estate', 'Cash', 'TIPS', 'Unclassified']);
  extendedStyles = orderLabels(extendedStyles, ['US Large Growth', 'US Large Value', 'US Mid Growth', 'US Mid Value', 'US Small Growth', 'US Small Value', 'Developed Market Stock', 'Emerging Market Stock', 'Corporate Bond', 'Municipal Bond', 'Government Bond', 'Securitized Bond', 'Real Estate', 'Cash', 'Unclassified']);

  const current = extendedStyles.map(styleCategory => {
    return { name: styleCategory, value: (byExtendedStyle?.[styleCategory] || 0) * 100 };
  });
  const proposal = extendedStyles.map(styleCategory => {
    return { name: styleCategory, value: (benchmarkbyExtendedStyle?.[styleCategory] || 0) * 100 };
  });
  const optimized = extendedStyles.map(styleCategory => {
    return { name: styleCategory, value: (optimizedbyExtendedStyle?.[styleCategory] || 0) * 100 };
  });

  const grade = 'A';

  const colors = [
    '#4A90E2', // Soft Blue
    '#FF6B6B', // Vibrant Coral
    '#F9A825', // Golden Yellow
    '#4CAF50', // Muted Green
    '#9B51E0', // Rich Purple
    '#BDBDBD', // Neutral Gray
    '#D32F2F', // Red
    '#1976D2', // Blue
    '#388E3C', // Green
    '#F57C00', // Orange
    '#8E44AD', // Amethyst
    '#E67E22', // Carrot Orange
    '#16A085', // Greenish Blue
    '#2ECC71', // Emerald
    '#3498DB', // Light Blue
    '#34495E', // Dark Gray
    '#E74C3C', // Light Red
    '#1ABC9C', // Light Green
    '#F1C40F', // Bright Yellow
  ];

  const colorsbyExtendedStyle = {
    'US Large Growth': '#4A90E2',
    'US Large Value': '#F9A825',
    'US Mid Growth': '#4CAF50',
    'US Mid Value': '#9B51E0',
    'US Small Growth': '#D32F2F',
    'US Small Value': '#388E3C',
    'Developed Market Stock': '#F57C00',
    'Emerging Market Stock': '#8E44AD',
    // 'US Investment Grade Bonds': '#E67E22',
    // 'US High Yield Bonds': '#16A085',
    // 'Non-US Bonds': '#2ECC71',
    'Corporate Bond': '#E67E22', 
    'Municipal Bond': '#16A085', 
    'Government Bond': '#2ECC71', 
    'Securitized Bond': '#9C27B0',
    'Real Estate': '#3498DB',
    'Cash': '#34495E',
    // 'TIPS': '#E74C3C',
    'Unclassified': '#BDBDBD',
  };

  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];
  const portfolioSeries = []; // For Highcharts
  const benchmarkSeries = []; // For Highcharts
  const optimizedSeries = []; // For Highcharts

  extendedStyles.forEach(styleCategory => {
    const portfolioValue = byExtendedStyle?.[styleCategory] || 0;
    const benchmarkValue = benchmarkbyExtendedStyle?.[styleCategory] || 0;
    const optimizedValue = optimizedbyExtendedStyle?.[styleCategory] || 0;

    portfolioData.push(portfolioValue);
    benchmarkData.push(benchmarkValue);
    optimizedData.push(optimizedValue);

    if (portfolioValue) portfolioSeries.push({ name: styleCategory, y: portfolioValue, color: colorsbyExtendedStyle[styleCategory] });
    if (benchmarkValue) benchmarkSeries.push({ name: styleCategory, y: benchmarkValue, color: colorsbyExtendedStyle[styleCategory] });
    if (optimizedValue) optimizedSeries.push({ name: styleCategory, y: optimizedValue, color: colorsbyExtendedStyle[styleCategory] });
  });

  const chartDataForPortfolio = portfolio ? {
    labels: extendedStyles,
    datasets: [{
      label: portfolio.name || 'Portfolio',
      data: portfolioData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'extendedStyles',
    }],
  } : null;

  const chartDataForBenchmark = benchmark ? {
    labels: extendedStyles,
    datasets: [{
      label: benchmark.name || 'Benchmark',
      data: benchmarkData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'extendedStyles',
    }],
  } : null;

  const chartDataForOptimized = optimizedPortfolio ? {
    labels: extendedStyles,
    datasets: [{
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedData,
      borderColor: 'white',
      borderWidth: 3,
      backgroundColor: colors,
      key: 'extendedStyles',
    }],
  } : null;

  const seriesDataForHighcharts = [];

  if (portfolio) {
    seriesDataForHighcharts.push({
      name: portfolio.name || 'Portfolio',
      data: portfolioSeries,
    });
  }

  if (benchmark) {
    seriesDataForHighcharts.push({
      name: benchmark.name || 'Benchmark',
      data: benchmarkSeries,
    });
  }

  if (optimizedPortfolio) {
    seriesDataForHighcharts.push({
      name: optimizedPortfolio.name || 'Optimized',
      data: optimizedSeries,
    });
  }

  // This ensures that the pie charts are evenly spaced horizontally regardless of how many there are
  const pieCount = seriesDataForHighcharts.length;
  const pieSize = 100;

  if (pieCount > 1) {
    let margin;
    if (pieCount === 2) {
      margin = 25;
    } else if (pieCount === 3) {
      margin = 15;
    }
    const usableWidth = 100 - margin * 2;
    const gap = usableWidth / (pieCount - 1); // Gap between pies

    for (let i = 0; i < pieCount; i++) {
      const series = seriesDataForHighcharts[i];
      const x = margin + gap * i;
      series.center = [`${x}%`, '50%']; // Position of the center of the pie
    }

    //
  } else {
    seriesDataForHighcharts[0].center = ['50%', '50%'];
  }

  const output = {
    chartDataForPortfolio,
    chartDataForBenchmark,
    chartDataForOptimized,
    seriesDataForHighcharts,
    pieSize,
    grade, current, proposal, optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: null,
    style: getGradeStyle(null),
    methodology: 'ExtendedStyleMethodology',
  };
  return output;
}

export async function getExplanation(area) {
  const areaCopy = { ...area };
  if (areaCopy.explanation) {
    delete areaCopy.explanation;
  }
  // Use the values from `portfolioName` and `benchmarkName` to replace `current` and `proposal` keys
  const portfolioKey = areaCopy.portfolioName;
  const benchmarkKey = areaCopy.benchmarkName;
  const optimizedKey = areaCopy.proposedName;

  if (portfolioKey && areaCopy.current) {
    areaCopy[portfolioKey] = areaCopy.current;
    delete areaCopy.current;
  }

  if (benchmarkKey && areaCopy.proposal) {
    areaCopy[benchmarkKey] = areaCopy.proposal;
    delete areaCopy.proposal;
  }

  if (optimizedKey && areaCopy.optimized) {
    areaCopy[optimizedKey] = areaCopy.optimized;
    delete areaCopy.optimized;
  }

  const areaParams = JSON.stringify(areaCopy);
  console.log(`Getting the ai explanation for ${area.name}`, JSON.parse(areaParams));
  const url = `/api/v1/checkup/ai-explanation/`;
  const body = { area: areaCopy };
  console.log('BODY', body);

  const data = await awaitTask(url, {
    'method': 'POST',
    'body': JSON.stringify(body),
    'headers': { 'Content-Type': 'application/json', 'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value },
  });
  return DOMPurify.sanitize(marked.parse(data.task.meta.result));
}


export function getRiskAnalytics(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const portfolioRisk = portfolio?.risk_score;
  const benchmarkRisk = benchmark?.risk_score;
  const optimizedRisk = optimizedPortfolio?.risk_score;

  const current = portfolio ? [{ value: portfolioRisk }] : [];
  const proposal = benchmark ? [{ value: benchmarkRisk }] : [];
  const optimized = optimizedPortfolio ? [{ value: optimizedRisk }] : [];

  const grade = portfolio && benchmark ? getRiskGrade(portfolio, benchmark) : null;

  const chartData = {
    type: 'risk',
    datasets: [],
  };

  if (portfolio) {
    chartData.datasets.push({
      label: portfolio.name || 'Portfolio',
      data: [portfolioRisk],
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      key: 'risk',
    });
  }

  if (benchmark) {
    chartData.datasets.push({
      label: benchmark.name || 'Benchmark',
      data: [benchmarkRisk],
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      key: 'risk',
    });
  }

  if (optimizedPortfolio) {
    chartData.datasets.push({
      label: optimizedPortfolio.name || 'Optimized',
      data: [optimizedRisk],
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      key: 'risk',
    });
  }

  const output = {
    chartData: chartData.datasets.length > 0 ? [chartData] : [],
    grade,
    current,
    proposal,
    optimized,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Risk'],
    style: getGradeStyle(null),
    methodology: 'RiskMethodology',
  };
  return output;
}


function getRiskGrade(portfolio, benchmark) {
  return null; // GRADES.GOOD;
}

function getTopTenHoldings(data, key) {
  const sortedPieChartData = [...data].sort((a, b) => b[key] - a[key]);
  const topTenPieChartData = sortedPieChartData.slice(0, 10); // TODO what if we have less than 10 holdings?
  const otherPieChartData = sortedPieChartData.slice(10);
  const otherPortfolioValue = otherPieChartData.reduce((total, data) => total + data[key], 0);
  // const otherBenchmarkValue = otherPieChartData.reduce((total, data) => total + data.benchmarkValue, 0);
  const pieChartDataForPortfolio = [...topTenPieChartData];
  return pieChartDataForPortfolio;
}

export function getConcentrationsBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byHolding = portfolio?.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] || {};
  const benchmarkByHolding = benchmark?.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] || {};
  const optimizedByHolding = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] || {};
  const namesOfHoldingsInBoth = Object.keys({ ...byHolding, ...benchmarkByHolding, ...optimizedByHolding });

  const n = 10;
  const current = portfolio ? getTopTickers(byHolding, n).map((ticker, index) => ({
    name: index === 0 ? `Top ${n} Hldgs.` : '',
    value: ticker,
  })) : [];

  const proposal = benchmark ? getTopTickers(benchmarkByHolding, n).map((ticker, index) => ({
    name: index === 0 ? `Top ${n} Hldgs.` : '',
    value: ticker,
  })) : [];

  const optimized = optimizedPortfolio ? getTopTickers(optimizedByHolding, n).map((ticker, index) => ({
    name: index === 0 ? `Top ${n} Hldgs.` : '',
    value: ticker,
  })) : [];

  const pieChartData = namesOfHoldingsInBoth.map(holding => {
    const colorForHolding = `rgb(${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)})`;
    const portfolioValue = byHolding?.[holding] || 0;
    const benchmarkValue = benchmarkByHolding?.[holding] || 0;
    const optimizedValue = optimizedByHolding?.[holding] || 0;
    return {
      holdingName: holding,
      colorForHolding,
      portfolioValue,
      benchmarkValue,
      optimizedValue,
    };
  });

  const pieChartDataForPortfolio = portfolio ? getTopTenHoldings(pieChartData, 'portfolioValue') : [];
  const pieChartDataForBenchmark = benchmark ? getTopTenHoldings(pieChartData, 'benchmarkValue') : [];
  const pieChartDataForOptimized = optimizedPortfolio ? getTopTenHoldings(pieChartData, 'optimizedValue') : [];

  const chartDataForPortfolio = portfolio ? {
    labels: pieChartDataForPortfolio.map(data => data.holdingName),
    datasets: [
      {
        data: pieChartDataForPortfolio.map(data => data.portfolioValue),
        backgroundColor: pieChartDataForPortfolio.map(data => data.colorForHolding),
        key: 'concentration',
      },
    ],
  } : null;

  const chartDataForBenchmark = benchmark ? {
    labels: pieChartDataForBenchmark.map(data => data.holdingName),
    datasets: [
      {
        data: pieChartDataForBenchmark.map(data => data.benchmarkValue),
        backgroundColor: pieChartDataForBenchmark.map(data => data.colorForHolding),
        key: 'concentration',
      },
    ],
  } : null;

  const chartDataForOptimized = optimizedPortfolio ? {
    labels: pieChartDataForOptimized.map(data => data.holdingName),
    datasets: [
      {
        data: pieChartDataForOptimized.map(data => data.optimizedValue),
        backgroundColor: pieChartDataForOptimized.map(data => data.colorForHolding),
        key: 'concentration',
      },
    ],
  } : null;

  const output = {
    chartDataForPortfolio,
    chartDataForBenchmark,
    chartDataForOptimized,
    current,
    proposal,
    optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: null,
    methodology: 'ConcentrationBreakdownMethodology',
  };
  return output;
}


function getTopTickers(holdings, n=10) {
  const sorted = Object.entries(holdings).sort((a, b) => b[1] - a[1]);
  const top = sorted.filter(holding => !!holding[0]).slice(0, n);
  const totalValue = (top.reduce((acc, holding) => acc + holding[1], 0) * 100).toFixed(2) + '%';
  return [totalValue, ...top.map(holding => {
    const ticker = holding[0];
    return ticker.length > 5 ? ticker.slice(0, 5) + '...' : ticker;
  })];
};

export function getGeoBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byRegion = portfolio?.sector_market_cap_asset_class_breakdown?.['geography_breakdown'] || {};
  const benchmarkByRegion = benchmark?.sector_market_cap_asset_class_breakdown?.['geography_breakdown'] || {};
  const optimizedByRegion = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['geography_breakdown'] || {};
  let regions = Object.keys({ ...byRegion, ...benchmarkByRegion, ...optimizedByRegion });
  regions = orderLabels(regions, ['United States', 'Developed', 'Emerging', 'Unclassified']);

  const current = portfolio ? regions.map(region => ({
    name: region,
    value: (byRegion[region] || 0) * 100,
  })) : [];

  const proposal = benchmark ? regions.map(region => ({
    name: region,
    value: (benchmarkByRegion[region] || 0) * 100,
  })) : [];

  const optimized = optimizedPortfolio ? regions.map(region => ({
    name: region,
    value: (optimizedByRegion[region] || 0) * 100,
  })) : [];

  const grade = null; // For now this, but maybe can do above line

  const chartData = {
    labels: regions,
    datasets: [],
  };

  if (portfolio) {
    const portfolioData = regions.map(region => byRegion[region] || 0);
    chartData.datasets.push({
      label: portfolio.name || 'Portfolio',
      data: portfolioData,
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      key: 'regions',
    });
  }

  if (benchmark) {
    const benchmarkData = regions.map(region => benchmarkByRegion[region] || 0);
    chartData.datasets.push({
      label: benchmark.name || 'Benchmark',
      data: benchmarkData,
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      key: 'regions',
    });
  }

  if (optimizedPortfolio) {
    const optimizedData = regions.map(region => optimizedByRegion[region] || 0);
    chartData.datasets.push({
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedData,
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      key: 'regions',
    });
  }

  const output = {
    chartData,
    grade,
    current,
    proposal,
    optimized,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    definition: definitions['Geography Breakdown'],
    style: getGradeStyle(null),
    methodology: 'GeographyMethodology',
  };
  return output;
}


export function getFixedIncomeTypeBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const byFIType = portfolio?.sector_market_cap_asset_class_breakdown?.['fi_type_breakdown'] || {};
  const benchmarkByFIType = benchmark?.sector_market_cap_asset_class_breakdown?.['fi_type_breakdown'] || {};
  const optimizedByFIType = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['fi_type_breakdown'] || {};
  let fiTypes = Object.keys({ ...byFIType, ...benchmarkByFIType, ...optimizedByFIType });
  // Placeholder order, to be updated later
  fiTypes = orderLabels(fiTypes, ['Corporate', 'Municipal', 'Government', 'Securitized', 'Unclassified']);

  const current = fiTypes.map(fiType => {
    return { name: fiType, value: (byFIType[fiType] || 0) * 100 };
  });
  const proposal = fiTypes.map(fiType => {
    return { name: fiType, value: (benchmarkByFIType[fiType] || 0) * 100 };
  });
  const optimized = fiTypes.map(fiType => {
    return { name: fiType, value: (optimizedByFIType[fiType] || 0) * 100 };
  });

  const portfolioData = [];
  const benchmarkData = [];
  const optimizedData = [];
  fiTypes.forEach(fiType => {
    portfolioData.push(byFIType?.[fiType] || 0);
    benchmarkData.push(benchmarkByFIType?.[fiType] || 0);
    optimizedData.push(optimizedByFIType?.[fiType] || 0);
  });

  const chartData = {
    labels: fiTypes,
    datasets: [],
  };

  if (portfolio) {
    chartData.datasets.push({
      label: portfolio.name || 'Portfolio',
      data: portfolioData,
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      key: 'fiTypes',
    });
  }

  if (benchmark) {
    chartData.datasets.push({
      label: benchmark.name || 'Benchmark',
      data: benchmarkData,
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      key: 'fiTypes',
    });
  }

  if (optimizedPortfolio) {
    chartData.datasets.push({
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedData,
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      key: 'fiTypes',
    });
  }

  const output = {
    chartData,
    current: portfolio ? current : null,
    proposal: benchmark ? proposal : null,
    optimized: optimizedPortfolio ? optimized : null,
    isPercent: true,
    cumulative_fee_difference: 'N/A',
    // Placeholder definitions and methodology, to be updated later
    definition: 'Fixed Income Type Breakdown',
    style: getGradeStyle(null),
    methodology: 'FixedIncomeTypeMethodology',
  };
  return output;
}


export function getFixedIncomeDurationBreakdown(portfolio, benchmark, optimizedPortfolio, advisorStore) {
  const durationBreakdown = portfolio?.sector_market_cap_asset_class_breakdown?.['duration_breakdown'] || {};
  const benchmarkDurationBreakdown = benchmark?.sector_market_cap_asset_class_breakdown?.['duration_breakdown'] || {};
  const optimizedDurationBreakdown = optimizedPortfolio?.sector_market_cap_asset_class_breakdown?.['duration_breakdown'] || {};

  const durationLabels = ['< 1 Year', '1-3 Years', '3-5 Years', '5-7 Years', '7-10 Years', '10-15 Years', '15-20 Years', '20-30 Years', '> 30 Years', 'Unclassified'];
  const portfolioDurations = [];
  const benchmarkDurations = [];
  const optimizedDurations = [];

  durationLabels.forEach(label => {
    portfolioDurations.push(durationBreakdown[label] || 0);
    benchmarkDurations.push(benchmarkDurationBreakdown[label] || 0);
    optimizedDurations.push(optimizedDurationBreakdown[label] || 0);
  });

  const chartData = {
    labels: durationLabels,
    datasets: [],
  };

  if (portfolio) {
    chartData.datasets.push({
      label: portfolio.name || 'Portfolio',
      data: portfolioDurations,
      borderColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.portfolio_color,
      key: 'duration',
    });
  }

  if (benchmark) {
    chartData.datasets.push({
      label: benchmark.name || 'Benchmark',
      data: benchmarkDurations,
      borderColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.benchmark_color,
      key: 'duration',
    });
  }

  if (optimizedPortfolio) {
    chartData.datasets.push({
      label: optimizedPortfolio.name || 'Optimized',
      data: optimizedDurations,
      borderColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      backgroundColor: advisorStore.firmAdvisorCoreSettings.proposed_color,
      key: 'duration',
    });
  }

  const current = portfolio ? durationLabels.map(label => {
    return { name: label, value: (durationBreakdown[label] || 0) * 100 };
  }) : null;

  const proposal = benchmark ? durationLabels.map(label => {
    return { name: label, value: (benchmarkDurationBreakdown[label] || 0) * 100 };
  }) : null;

  const optimized = optimizedPortfolio ? durationLabels.map(label => {
    return { name: label, value: (optimizedDurationBreakdown[label] || 0) * 100 };
  }) : null;

  return {
    chartData,
    current,
    proposal,
    optimized,
    cumulative_fee_difference: 'N/A',
    isPercent: true,
    definition: 'Fixed Income Maturity Breakdown',
    style: getGradeStyle(null),
    methodology: 'FixedIncomeDurationMethodology',
  };
}


function orderLabels(labels, preferredOrder) {
  const ordered = [];

  preferredOrder.forEach(label => {
    if (labels.includes(label)) ordered.push(label);
  });

  labels.forEach(label => {
    if (!preferredOrder.includes(label)) ordered.push(label);
  });

  return ordered;
}
